import * as React from 'react';
import { connect } from 'react-redux';
import AppState from 'src/redux/AppState';
import Settings from 'src/Settings';
import RecentSearch from './RecentSearch';
import SearchLanguage from './SearchLanguage';
import SearchOptions from './SearchOptions';
import SearchQuery from './SearchQuery';
import './SourceSearch.css';
import SourceSearchPanel from './SourceSearchPanel';

interface OwnProps {
    onSearch: (query: SearchQuery) => void;
}

interface StateProps {
    visibleVersions: number[];
}

const mapStateToProps = (state: AppState): StateProps => ({
    visibleVersions: state.settings.visibleVersions
});

type Props = OwnProps & StateProps;

interface State {
    //language: SearchLanguage;
    searchText: string;
    options: SearchOptions;
    showPanel: boolean;
    recentSearches: RecentSearch[];
}

class SourceSearch extends React.Component<Props, State> {

    private readonly settings = new Settings();
    private readonly languageSearchText = new Map<SearchLanguage, string>();
    //private hidePanelTimeout: NodeJS.Timer;
    private textBox: HTMLInputElement;

    public constructor(props: Props) {
        super(props);
        this.state = {
            //language: SearchLanguage.English,
            searchText: '',
            options: {
                language: SearchLanguage.English,
                versionIndexes: props.visibleVersions,
                searchAllVersions: true,
                searchStems: false
            },
            showPanel: false,
            recentSearches: this.settings.getRecentSourceSearches()
        };
        //this.getLanguageClass = this.getLanguageClass.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onOptionsChange = this.onOptionsChange.bind(this);
        //this.onLanguageClick = this.onLanguageClick.bind(this);
        this.onRecentSearchClick = this.onRecentSearchClick.bind(this);
        this.onRecentSearchRemoveClick = this.onRecentSearchRemoveClick.bind(this);
        this.onPanelClickAway = this.onPanelClickAway.bind(this);
        this.doSearch = this.doSearch.bind(this);
        this.closePanel = this.closePanel.bind(this);
    }

    public static getDerivedStateFromProps(nextProps: Readonly<Props>, prevState: Readonly<State>): Partial<State> | null {
        return { options: { ...prevState.options, versionIndexes: nextProps.visibleVersions }};
    }

    public render() {
        return (
            <div className="SourceSearch">
                <input id="SourceSearch" type="text" value={this.state.searchText}
                    placeholder={this.isTextBoxFocused() ? '' : 'Search for words'}
                    className={this.getLanguageClass(this.isTextBoxFocused() || this.state.searchText !== '' ? this.state.options.language : SearchLanguage.English)}
                    onChange={this.onChange}
                    onKeyUp={this.onKeyUp}
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    ref={element => this.textBox = element as HTMLInputElement} />
                {this.state.showPanel && <SourceSearchPanel
                    searchBoxId="SourceSearch"
                    options={this.state.options}
                    //currentLanguage={this.state.options.language}
                    recentSearches={this.state.recentSearches}
                    onOptionsChange={this.onOptionsChange}
                    //onLanguageClick={this.onLanguageClick}
                    onRecentSearchClick={this.onRecentSearchClick}
                    onRecentSearchRemoveClick={this.onRecentSearchRemoveClick}
                    onClickAway={this.onPanelClickAway} />}
            </div>
        );
    }

    private isTextBoxFocused(): boolean {
        return document.activeElement === this.textBox;
    }

    private getLanguageClass(language: SearchLanguage): string {
        switch (language) {
            case SearchLanguage.Hebrew:
                return '_Hebrew';
            case SearchLanguage.Greek:
                return '_Greek';
            case SearchLanguage.English:
            default:
                return 'SourceSearch_English';
        }
    }

    private onChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({ searchText: event.target.value });
    }

    private onKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.keyCode === 13) {
            this.doSearch();
        }
    }

    private onFocus() {
        //clearTimeout(this.hidePanelTimeout);
        this.setState({ showPanel: true });
    }

    private onBlur() {
        //this.hidePanelTimeout = setTimeout(() => this.setState({ showPanel: false }), 500);
    }

    private onOptionsChange(options: SearchOptions) {
        //this.setState({ options });
        this.languageSearchText.set(this.state.options.language, this.state.searchText);
        const searchText = this.languageSearchText.get(options.language) || '';
        this.setState({
            searchText,
            options
        }); //, () => this.doSearch());
        this.textBox.focus();
    }

    /*private onLanguageClick(language: SearchLanguage) {
        this.languageSearchText.set(this.state.language, this.state.searchText);
        const searchText = this.languageSearchText.get(language) || this.state.searchText;
        this.setState({
            language,
            searchText
        }, () => this.doSearch());
    }*/

    private onRecentSearchClick(search: RecentSearch) {
        this.setState({
            options: search.options,
            searchText: search.text
        }, () => this.doSearch());
    }

    private onRecentSearchRemoveClick(search: RecentSearch) {
        this.textBox.focus(); // Do not close the search panel.
        this.setState(prevState => {
            const recentSearches = prevState.recentSearches.filter(existing => existing.getId() !== search.getId());
            this.settings.setRecentSourceSearches(recentSearches);
            return { recentSearches };
        });
    }

    private onPanelClickAway() {
        if (!this.isTextBoxFocused()) {
            this.closePanel();
        }
    }

    private doSearch() {
        this.textBox.blur();
        this.closePanel();
        if (this.state.searchText.trim() !== '') {
            // Add search to recent searches.
            const newSearch = new RecentSearch(this.state.searchText, this.state.options);
            this.setState(prevState => {
                // Move new search to the top of recent searches.
                const recentSearches = Array.of(newSearch).concat(
                        prevState.recentSearches.filter(search => search.getId() !== newSearch.getId()))
                this.settings.setRecentSourceSearches(recentSearches);
                return { recentSearches };
            });
        }
        this.props.onSearch(new SearchQuery(this.state.searchText, this.state.options));
    }

    private closePanel() {
        this.setState({ showPanel: false });
    }
}

export default connect(mapStateToProps)(SourceSearch);
